import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { C } from "../constants/colors";
import { css } from "../constants/globalStyles";
import { Logo } from "../components/ui/Logo";
import { LogoDark } from "../components/ui/LogoDark";

export default function ResetPasswordPage() {
  const [checking, setChecking]   = useState(true);
  const [invalid, setInvalid]     = useState(false);
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [showPw, setShowPw]       = useState(false);
  const [errors, setErrors]       = useState({});
  const [loading, setLoading]     = useState(false);
  const [success, setSuccess]     = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) setInvalid(true);
      setChecking(false);
    });
  }, []);

  const submit = async () => {
    const e = {};
    if (!password) e.password = "Required";
    else if (password.length < 8) e.password = "Password must be at least 8 characters";
    if (!confirm) e.confirm = "Required";
    else if (password !== confirm) e.confirm = "Passwords don't match";
    setErrors(e);
    if (Object.keys(e).length) return;

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      setErrors({ general: error.message });
    } else {
      setSuccess(true);
    }
  };

  if (checking) {
    return (
      <>
        <style>{css}</style>
        <div style={{ minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:C.parch }}>
          <span style={{ fontFamily:"'DM Mono',monospace",fontSize:".7rem",color:C.muted,letterSpacing:"2px",textTransform:"uppercase" }}>Loading…</span>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="auth-layout">

        {/* Visual panel — identical to LoginScreen */}
        <div className="auth-visual">
          <div><Logo /></div>
          <div style={{ position:"relative",zIndex:1 }}>
            <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1.35rem",color:"#f0e2cc",fontWeight:300,lineHeight:1.5,marginBottom:14,fontStyle:"italic" }}>
              "opencall. completely changed how I find exhibition opportunities."
            </div>
            <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".68rem",color:C.clay,letterSpacing:"1.5px",textTransform:"uppercase" }}>Yindi Nguyen</div>
            <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".6rem",color:"rgba(196,168,130,.4)",marginTop:3 }}>Painter & Printmaker · Naarm / Melbourne</div>
          </div>
          <div style={{ display:"flex",gap:24 }}>
            {[["320+","Open calls"],["2,400+","Artists"],["190+","Galleries"]].map(([n,l])=>(
              <div key={l}>
                <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1.3rem",color:C.clay }}>{n}</div>
                <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".56rem",color:"rgba(196,168,130,.4)",letterSpacing:"1.5px",textTransform:"uppercase",marginTop:2 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Form panel */}
        <div className="auth-panel">
          <div style={{ maxWidth:400,width:"100%" }}>
            <div style={{ marginBottom:28 }}><LogoDark /></div>

            {invalid ? (
              /* Invalid / expired link */
              <>
                <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.9rem",fontWeight:400,color:C.bark,marginBottom:6 }}>Link expired</h1>
                <p style={{ fontSize:".85rem",color:C.muted,marginBottom:28,lineHeight:1.65 }}>
                  This reset link is invalid or has expired. Request a new one from the login page.
                </p>
                <Link
                  to="/login"
                  className="btn-p"
                  style={{ width:"100%",textDecoration:"none",justifyContent:"center" }}
                >
                  Back to log in
                </Link>
              </>
            ) : success ? (
              /* Success state */
              <>
                <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.9rem",fontWeight:400,color:C.bark,marginBottom:6 }}>Password updated</h1>
                <div style={{ background:C.sageL,border:`1px solid ${C.sage}`,borderRadius:6,padding:"14px 16px",marginBottom:28,display:"flex",alignItems:"center",gap:10 }}>
                  <span style={{ fontSize:"1rem" }}>✓</span>
                  <div style={{ fontSize:".85rem",color:C.sageD,lineHeight:1.55 }}>Your password has been updated.</div>
                </div>
                <Link
                  to="/login"
                  className="btn-p"
                  style={{ width:"100%",textDecoration:"none",justifyContent:"center" }}
                >
                  Log in →
                </Link>
              </>
            ) : (
              /* Reset form */
              <>
                <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.9rem",fontWeight:400,color:C.bark,marginBottom:6 }}>Set new password</h1>
                <p style={{ fontSize:".85rem",color:C.muted,marginBottom:28,lineHeight:1.65 }}>Choose a strong password for your opencall. account.</p>

                {errors.general && (
                  <div style={{ background:C.roseL,border:`1px solid ${C.rose}`,borderRadius:5,padding:"10px 14px",marginBottom:16,fontSize:".8rem",color:C.rose }}>⚠ {errors.general}</div>
                )}

                <div className="form-group">
                  <label className="form-label">New password</label>
                  <div style={{ position:"relative" }}>
                    <input
                      className={`form-input${errors.password?" err":""}`}
                      type={showPw?"text":"password"}
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={e=>{setPassword(e.target.value);setErrors({});}}
                      onKeyDown={e=>e.key==="Enter"&&submit()}
                    />
                    <button
                      onClick={()=>setShowPw(!showPw)}
                      style={{ position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:C.muted,minHeight:36,minWidth:36,display:"flex",alignItems:"center",justifyContent:"center" }}
                    >{showPw?"🙈":"👁️"}</button>
                  </div>
                  {errors.password&&<div style={{ fontSize:".72rem",color:C.rose,marginTop:4 }}>⚠ {errors.password}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">Confirm password</label>
                  <input
                    className={`form-input${errors.confirm?" err":""}`}
                    type={showPw?"text":"password"}
                    placeholder="Repeat your password"
                    value={confirm}
                    onChange={e=>{setConfirm(e.target.value);setErrors({});}}
                    onKeyDown={e=>e.key==="Enter"&&submit()}
                  />
                  {errors.confirm&&<div style={{ fontSize:".72rem",color:C.rose,marginTop:4 }}>⚠ {errors.confirm}</div>}
                </div>

                <button className="btn-p" style={{ width:"100%",marginTop:8 }} onClick={submit} disabled={loading}>
                  {loading?"Updating…":"Update password →"}
                </button>
              </>
            )}
          </div>
        </div>

      </div>
    </>
  );
}
